import { useEffect, useMemo, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Loader2, RotateCcw, Send } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  eventSettingsQueryOptions,
  INVITE_PREVIEW_VARIANTS,
  patchEvent,
  previewInviteEmail,
  sendTestInviteEmail,
  type EventSettingsRow,
  type InvitePreviewVariant,
} from "@/core/settings"
import { ApiError } from "@/core/api"
import {
  Card,
  Field,
  GhostButton,
  PrimaryButton,
  SectionPage,
  SectionPageFooter,
  Select,
  TextInput,
} from "@/components/settings/primitives"
import { ImageUploadField } from "@/components/settings/ImageUploadField"

// Invite & guest-email copy, subjects, preview + send-test.
//
// The inputs are PRE-FILLED with the resolved smart defaults (which adapt
// to this event's features — no "pick your activities" when the schedule
// is off), so organizers edit real copy in place. On save, only fields
// that DIFFER from their default are stored as overrides — untouched
// fields keep tracking the defaults if the event's features change later.
// Stored as one JSON blob on events.inviteCopy; the server sanitizes on
// read (quickback events/lib/invite-copy.ts — keep the default strings
// below in sync with that file).

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/invites")({
  component: InvitesView,
})

type CopyForm = {
  welcomeLine: string
  introParagraph: string // the invitation message; multi-line
  benefits: string // one per line
  step3Label: string
  ctaLabel: string
  teamLabel: string
  // Rich invite email — step + CTA copy
  getStartedLabel: string
  step1Label: string
  appBlurb: string // multi-line
  downloadCtaLabel: string
  step2Label: string
  step2Body: string // multi-line
  declineLabel: string
  // Framed hero headlines (per audience)
  confirmHeadline: string
  goingHeadline: string
  waitlistHeadline: string
  plusOneHeadline: string
  // Subject lines — {event} (+ {inviter} on plus-one) fill in at send time.
  inviteSubject: string
  confirmPendingSubject: string
  goingSubject: string
  waitlistSubject: string
  plusOneSubject: string
  approvedSubject: string
  waitlistPromotedSubject: string
  importWelcomeSubject: string
  paidConfirmSubject: string
  confirmReminderSubject: string
}

const FIELDS: (keyof CopyForm)[] = [
  "welcomeLine",
  "introParagraph",
  "benefits",
  "step3Label",
  "ctaLabel",
  "teamLabel",
  "getStartedLabel",
  "step1Label",
  "appBlurb",
  "downloadCtaLabel",
  "step2Label",
  "step2Body",
  "declineLabel",
  "confirmHeadline",
  "goingHeadline",
  "waitlistHeadline",
  "plusOneHeadline",
  "inviteSubject",
  "confirmPendingSubject",
  "goingSubject",
  "waitlistSubject",
  "plusOneSubject",
  "approvedSubject",
  "waitlistPromotedSubject",
  "importWelcomeSubject",
  "paidConfirmSubject",
  "confirmReminderSubject",
]

// Subject-line fields, in display order — keep defaults in sync with
// DEFAULT_SUBJECTS in quickback events/lib/invite-copy.ts.
const SUBJECT_FIELDS: { key: keyof CopyForm; label: string; hint: string }[] = [
  { key: "inviteSubject", label: "Invitation", hint: "The first invite email." },
  { key: "confirmPendingSubject", label: "Confirm your spot", hint: "Invite (re)sent to a guest who is confirmed on paper but hasn't opened the app yet." },
  { key: "goingSubject", label: "Already going", hint: "Invite resent to a guest who has confirmed and opened the app." },
  { key: "waitlistSubject", label: "Waitlisted", hint: "Invite resent to a waitlisted guest." },
  { key: "plusOneSubject", label: "Plus-one invite", hint: "When one guest invites another. {inviter} is the host guest's name." },
  { key: "approvedSubject", label: "Approved", hint: "Sent when you approve a pending guest (without a full invite)." },
  { key: "waitlistPromotedSubject", label: "Waitlist spot opened", hint: "Sent when you approve a waitlisted guest." },
  { key: "importWelcomeSubject", label: "Imported guest welcome", hint: "Sent to guests added as confirmed in bulk (import / add confirmed)." },
  { key: "paidConfirmSubject", label: "Ticket purchased", hint: "Sent after a guest buys a ticket." },
  { key: "confirmReminderSubject", label: "Confirm-spot reminder", hint: "The default for the scheduled confirm-your-spot reminders (a reminder can set its own subject in Settings → Reminders)." },
]

const MAX_LINE = 300
// Email banner cap — mirrors EMAIL_BANNER_MAX_BYTES in quickback
// events/lib/invite-copy.ts (the send path drops anything larger).
const EMAIL_BANNER_MAX_BYTES = 1024 * 1024

type BannerPosition = "above" | "below"

function parseBanner(raw: string | null | undefined): { url: string; position: BannerPosition } {
  try {
    const j = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
    return {
      url: typeof j.emailBannerUrl === "string" ? j.emailBannerUrl : "",
      position: j.emailBannerPosition === "below" ? "below" : "above",
    }
  } catch {
    return { url: "", position: "above" }
  }
}
const MAX_MESSAGE = 1000
// Fields that allow a longer, multi-line value (higher cap, textarea input).
const LONG_FIELDS: (keyof CopyForm)[] = ["introParagraph", "appBlurb", "step2Body"]

// Mirror of the server's flag-aware defaults (quickback
// events/lib/invite-copy.ts) using this event's real data — what actually
// renders when no override is saved.
function resolveDefaults(row: EventSettingsRow): CopyForm {
  const scheduleOn = (row.enableSchedule ?? 1) === 1
  const travelOn = (row.enableTravel ?? 1) === 1
  const conciergeOn = (row.enableConcierge ?? 1) === 1

  const benefits: string[] = []
  if (scheduleOn) {
    benefits.push("Your full schedule & itinerary", "Pick your activities")
  } else {
    benefits.push("Event details & updates")
  }
  if (travelOn) benefits.push("Your flights & transfers")
  if (conciergeOn) benefits.push("Message the events team")

  return {
    // The lander greets each guest personally — {first name} is filled per
    // guest at render time.
    welcomeLine: "Hi {first name} — you're invited",
    // The invitation message — mirrors inviteEmailIntro's "invite" default.
    introParagraph: scheduleOn
      ? "You're invited! RSVP in the Attend app to lock in your spot and pick your activities — here's how."
      : "You're invited! RSVP in the Attend app to lock in your spot — here's how.",
    benefits: benefits.slice(0, 4).join("\n"),
    step3Label: scheduleOn ? "RSVP & pick your activities" : "RSVP & see event details",
    ctaLabel: "Confirm your spot in the app",
    teamLabel: "Event team",
    // Rich invite email — step + CTA copy (mirrors compose-invite.ts defaults).
    getStartedLabel: "Get started",
    step1Label: "Get the Attend Events app",
    appBlurb: scheduleOn
      ? "Download the Attend app to confirm your spot, complete your details, view your itinerary, make activity selections, get updates, and more."
      : "Download the Attend app to confirm your spot, complete your details, get updates, and more.",
    downloadCtaLabel: "Download Now",
    step2Label: "RSVP & join the event",
    step2Body: "Scan the QR code in the app to sign in and RSVP.",
    declineLabel: "Can't make it?",
    // Framed hero headlines (per audience).
    confirmHeadline: "One last step to confirm your spot",
    goingHeadline: "You're going",
    waitlistHeadline: "You're on the waitlist",
    plusOneHeadline: "{inviter} wants you to join them",
    inviteSubject: "You're invited: {event}",
    confirmPendingSubject: "One last step to confirm your spot — {event}",
    goingSubject: "You're going: {event}",
    waitlistSubject: "You're on the waitlist: {event}",
    plusOneSubject: "{inviter} wants you to join them at {event}",
    approvedSubject: "One last step to confirm your spot — {event}",
    waitlistPromotedSubject: "A spot opened up — confirm in the app — {event}",
    importWelcomeSubject: "One last step to confirm your spot — {event}",
    paidConfirmSubject: "One last step to confirm your spot — {event}",
    confirmReminderSubject: "Don't forget to confirm your spot — {event}",
  }
}

// Events saved before the single message field may carry a separate
// `personalMessage` that rendered above the intro; fold it in the same way the
// server does (parseInviteCopy) so the editor shows what guests receive.
// Saving writes only `introParagraph`, which retires the legacy key.
function invitationMessage(j: Record<string, unknown>): string | undefined {
  const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined)
  const note = text(j.personalMessage)
  const intro = text(j.introParagraph)
  if (note && intro) {
    const joined = `${note}\n\n${intro}`
    return joined.length <= MAX_MESSAGE ? joined : note
  }
  return note ?? intro
}

// Overrides stored on the event, mapped onto the form shape (missing →
// empty string, meaning "no override").
function parseOverrides(raw: string | null | undefined): Partial<CopyForm> {
  if (!raw) return {}
  try {
    const j = JSON.parse(raw) as Record<string, unknown>
    const line = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined)
    return {
      welcomeLine: line(j.welcomeLine),
      introParagraph: invitationMessage(j),
      benefits: Array.isArray(j.benefits)
        ? j.benefits.filter((b) => typeof b === "string").join("\n") || undefined
        : undefined,
      step3Label: line(j.step3Label),
      ctaLabel: line(j.ctaLabel),
      teamLabel: line(j.teamLabel),
      getStartedLabel: line(j.getStartedLabel),
      step1Label: line(j.step1Label),
      appBlurb: line(j.appBlurb),
      downloadCtaLabel: line(j.downloadCtaLabel),
      step2Label: line(j.step2Label),
      step2Body: line(j.step2Body),
      declineLabel: line(j.declineLabel),
      confirmHeadline: line(j.confirmHeadline),
      goingHeadline: line(j.goingHeadline),
      waitlistHeadline: line(j.waitlistHeadline),
      plusOneHeadline: line(j.plusOneHeadline),
      inviteSubject: line(j.inviteSubject),
      confirmPendingSubject: line(j.confirmPendingSubject),
      goingSubject: line(j.goingSubject),
      waitlistSubject: line(j.waitlistSubject),
      plusOneSubject: line(j.plusOneSubject),
      approvedSubject: line(j.approvedSubject),
      waitlistPromotedSubject: line(j.waitlistPromotedSubject),
      importWelcomeSubject: line(j.importWelcomeSubject),
      paidConfirmSubject: line(j.paidConfirmSubject),
      confirmReminderSubject: line(j.confirmReminderSubject),
    }
  } catch {
    return {}
  }
}

// Persist ONLY the fields that differ from their resolved default; a field
// left at (or cleared back to) the default stores nothing and keeps
// tracking the smart default.
function serializeDiff(form: CopyForm, defaults: CopyForm): string | null {
  const out: Record<string, unknown> = {}
  for (const key of FIELDS) {
    const value = form[key].trim()
    const fallback = defaults[key].trim()
    if (!value || value === fallback) continue
    if (key === "benefits") {
      const lines = value
        .split("\n")
        .map((b) => b.trim())
        .filter(Boolean)
        .slice(0, 6)
      if (lines.length && lines.join("\n") !== fallback) out.benefits = lines
    } else {
      out[key] = value
    }
  }
  return Object.keys(out).length ? JSON.stringify(out) : null
}

// The exact inviteCopy JSON a save would store — also what the live preview
// renders, so "what you see" is "what you'll save". null = no overrides.
function buildInviteCopyJson(
  form: CopyForm,
  defaults: CopyForm,
  banner: { url: string; position: BannerPosition },
): string | null {
  const copy = JSON.parse(serializeDiff(form, defaults) ?? "{}") as Record<string, unknown>
  const bannerUrl = banner.url.trim()
  if (bannerUrl) {
    copy.emailBannerUrl = bannerUrl
    copy.emailBannerPosition = banner.position
  }
  return Object.keys(copy).length ? JSON.stringify(copy) : null
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function InvitesView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const q = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const [form, setForm] = useState<CopyForm | null>(null)
  const [defaults, setDefaults] = useState<CopyForm | null>(null)
  const [dirty, setDirty] = useState(false)
  const [banner, setBanner] = useState<{ url: string; position: BannerPosition }>({
    url: "",
    position: "above",
  })

  useEffect(() => {
    if (!q.data || dirty) return
    setBanner(parseBanner(q.data.inviteCopy))
    const d = resolveDefaults(q.data)
    const overrides = parseOverrides(q.data.inviteCopy)
    setDefaults(d)
    setForm(
      Object.fromEntries(FIELDS.map((k) => [k, overrides[k] ?? d[k]])) as CopyForm,
    )
  }, [q.data, dirty])

  const save = useMutation({
    mutationFn: () => {
      if (!eventId || !form || !defaults) throw new Error("Not ready")
      // One-line fields (everything except benefits + the long multi-line ones)
      // are capped at MAX_LINE; the long fields get the higher MAX_MESSAGE cap.
      const tooLong = FIELDS.find(
        (key) =>
          key !== "benefits" &&
          !LONG_FIELDS.includes(key) &&
          form[key].trim().length > MAX_LINE,
      )
      if (tooLong) throw new ApiError(`Keep each line under ${MAX_LINE} characters`, 400)
      const longTooLong = LONG_FIELDS.find((key) => form[key].trim().length > MAX_MESSAGE)
      if (longTooLong) throw new ApiError(`Keep that message under ${MAX_MESSAGE} characters`, 400)
      const bannerUrl = banner.url.trim()
      if (bannerUrl && !bannerUrl.startsWith("https://")) {
        throw new ApiError("Email banner must be an https:// image URL", 400)
      }
      return patchEvent(eventId, {
        inviteCopy: buildInviteCopyJson(form, defaults, banner),
      })
    },
    onSuccess: () => {
      setDirty(false)
      toast.success("Invite copy saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to save"),
  })

  // ── Live preview (server-rendered, unsaved edits included) ──
  const [variant, setVariant] = useState<InvitePreviewVariant>("invite")
  const draftCopy = useMemo(
    () => (form && defaults ? buildInviteCopyJson(form, defaults, banner) : undefined),
    [form, defaults, banner],
  )
  const debouncedCopy = useDebounced(draftCopy, 450)
  const previewQ = useQuery({
    queryKey: ["invite-preview", eventId, variant, debouncedCopy ?? null],
    queryFn: () => previewInviteEmail({ eventId: eventId!, variant, inviteCopy: debouncedCopy ?? null }),
    enabled: !!eventId && debouncedCopy !== undefined,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
  const sendTest = useMutation({
    mutationFn: () => sendTestInviteEmail({ eventId: eventId!, variant, inviteCopy: draftCopy ?? null }),
    onSuccess: (r) => toast.success(`Test sent to ${r.to}`),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't send the test"),
  })

  const update = (patch: Partial<CopyForm>) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, ...patch } : f))
  }

  // Which fields currently differ from their default — drives the per-field
  // "customized" affordance + reset.
  const customized = (key: keyof CopyForm) =>
    !!form && !!defaults && form[key].trim() !== defaults[key].trim()

  const resetField = (key: keyof CopyForm) => {
    if (!defaults) return
    update({ [key]: defaults[key] } as Partial<CopyForm>)
  }

  const fieldLabel = (
    label: string,
    key: keyof CopyForm,
  ): React.ReactNode => (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {customized(key) && (
        <>
          <span className="rounded bg-[var(--accent)]/10 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[var(--accent)] uppercase">
            Customized
          </span>
          <button
            type="button"
            title="Reset to default"
            onClick={() => resetField(key)}
            className="text-plum-700/40 transition hover:text-plum-700 dark:text-plum-200/30 dark:hover:text-plum-200"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
        </>
      )}
    </span>
  )

  return (
    <SectionPage
      title="Invites & guest emails"
      description="What guests read before they're in the app — the invite email, lifecycle nudges, and the invite landing page. Edit any line; untouched lines keep the smart default, which adapts to this event's features."
    >
      <Card title="Preview & test">
        <p className="-mt-1 mb-1 text-[12.5px] leading-relaxed text-plum-700/55 dark:text-plum-200/40">
          Exactly what guests receive, including edits you haven't saved yet. The sign-in code and
          QR are placeholders. “Send test to me” emails this version to you only — no guest is added
          and nothing is billed. Works before the event is published.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[220px] flex-1">
            <Field label="Email">
              <Select value={variant} onChange={(e) => setVariant(e.target.value as InvitePreviewVariant)}>
                {INVITE_PREVIEW_VARIANTS.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <GhostButton
            type="button"
            disabled={!eventId || !form || sendTest.isPending}
            onClick={() => sendTest.mutate()}
          >
            {sendTest.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send test to me
          </GhostButton>
        </div>
        <div className="rounded-lg border border-plum-200/70 bg-plum-50/50 px-3 py-2 text-[12.5px] dark:border-white/10 dark:bg-white/[0.03]">
          <span className="text-plum-700/55 dark:text-plum-200/40">Subject: </span>
          <span className="font-semibold text-plum-900 dark:text-plum-50">
            {previewQ.data?.subject ?? (previewQ.isError ? "—" : "…")}
          </span>
          {previewQ.data && (
            <span className="ml-2 text-plum-700/45 dark:text-plum-200/35">from {previewQ.data.fromName}</span>
          )}
        </div>
        <div className="relative overflow-hidden rounded-lg border border-plum-200/70 bg-white dark:border-white/10">
          {previewQ.isFetching && (
            <Loader2 className="absolute top-2 right-2 h-4 w-4 animate-spin text-plum-700/40" />
          )}
          {previewQ.isError ? (
            <p className="p-4 text-[12.5px] text-rose-600 dark:text-rose-400">
              {previewQ.error instanceof Error ? previewQ.error.message : "Couldn't render the preview."}
            </p>
          ) : (
            <iframe
              title="Email preview"
              // Sandboxed, no scripts: server-rendered email HTML only.
              sandbox=""
              srcDoc={previewQ.data?.html ?? ""}
              className="block h-[560px] w-full bg-white"
            />
          )}
        </div>
      </Card>

      <Card title="Email banner">
        <p className="-mt-1 mb-1 text-[12.5px] leading-relaxed text-plum-700/55 dark:text-plum-200/40">
          A branding image shown full-width in every guest email — invites, reminders and
          confirmations. It's embedded in the email itself, so guests see it without
          loading remote images. Replaces the square event cover in emails.
        </p>
        {eventId && (
          <ImageUploadField
            eventId={eventId}
            label="Banner image"
            hint="PNG, JPG or GIF, max 1 MB — about 1000px wide works well. Pasted URLs over 1 MB are skipped when sending."
            value={banner.url}
            preview="banner"
            accept="image/png,image/jpeg,image/gif"
            maxBytes={EMAIL_BANNER_MAX_BYTES}
            onChange={(url) => {
              setDirty(true)
              setBanner((b) => ({ ...b, url }))
            }}
          />
        )}
        <Field label="Position" hint="Where the banner sits relative to the email headline.">
          <Select
            value={banner.position}
            disabled={!form || !banner.url.trim()}
            onChange={(e) => {
              setDirty(true)
              setBanner((b) => ({ ...b, position: e.target.value as BannerPosition }))
            }}
          >
            <option value="above">Above the headline</option>
            <option value="below">Below the headline</option>
          </Select>
        </Field>
      </Card>

      <Card title="Invite email">
        <p className="-mt-1 mb-1 text-[12.5px] leading-relaxed text-plum-700/55 dark:text-plum-200/40">
          The email a guest receives when they're invited. The hero headline adapts to
          where each guest is — a fresh invite, a pending confirmation, already going,
          or waitlisted.
        </p>
        <Field
          label={fieldLabel("Headline — confirm your spot", "confirmHeadline")}
          hint="Shown to guests who still need to confirm — default “One last step to confirm your spot”."
        >
          <TextInput
            value={form?.confirmHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ confirmHeadline: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Headline — going", "goingHeadline")}
          hint="Shown to guests who've already confirmed — default “You're going”."
        >
          <TextInput
            value={form?.goingHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ goingHeadline: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Headline — waitlist", "waitlistHeadline")}
          hint="Shown to waitlisted guests — default “You're on the waitlist”."
        >
          <TextInput
            value={form?.waitlistHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ waitlistHeadline: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Headline — plus-one invite", "plusOneHeadline")}
          hint="Shown when one guest invites another. Use {inviter} for the host's name — default “{inviter} wants you to join them”."
        >
          <TextInput
            value={form?.plusOneHeadline ?? ""}
            disabled={!form}
            onChange={(e) => update({ plusOneHeadline: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Invitation message", "introParagraph")}
          hint="Shown under the guest's name in the invitation email, before the steps. Replace the default with your own message; line breaks are kept. Waitlist, plus-one and “You're going” emails keep their own line."
        >
          <textarea
            value={form?.introParagraph ?? ""}
            disabled={!form}
            rows={4}
            onChange={(e) => update({ introParagraph: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Section eyebrow", "getStartedLabel")}
          hint='The small label above the two-step block — default "Get started".'
        >
          <TextInput
            value={form?.getStartedLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ getStartedLabel: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Step 1 header", "step1Label")}
          hint='The "1." step heading — default "Get the Attend Events app".'
        >
          <TextInput
            value={form?.step1Label ?? ""}
            disabled={!form}
            onChange={(e) => update({ step1Label: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("App blurb", "appBlurb")}
          hint="The paragraph under step 1 describing what the app does."
        >
          <textarea
            value={form?.appBlurb ?? ""}
            disabled={!form}
            rows={3}
            onChange={(e) => update({ appBlurb: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Download button", "downloadCtaLabel")}
          hint='The step 1 download button — default "Download Now".'
        >
          <TextInput
            value={form?.downloadCtaLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ downloadCtaLabel: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Step 2 header", "step2Label")}
          hint='The "2." step heading — default "RSVP & join the event".'
        >
          <TextInput
            value={form?.step2Label ?? ""}
            disabled={!form}
            onChange={(e) => update({ step2Label: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Step 2 instruction", "step2Body")}
          hint="The line above the invite code / QR box telling guests how to RSVP."
        >
          <textarea
            value={form?.step2Body ?? ""}
            disabled={!form}
            rows={2}
            onChange={(e) => update({ step2Body: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Decline link", "declineLabel")}
          hint={`The link under the code box for guests who can't attend — default "Can't make it?".`}
        >
          <TextInput
            value={form?.declineLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ declineLabel: e.target.value })}
          />
        </Field>
      </Card>

      <Card title="Email subject lines">
        <p className="-mt-1 mb-1 text-[12.5px] leading-relaxed text-plum-700/55 dark:text-plum-200/40">
          The subject each guest email goes out with. Use {"{event}"} for the event name.
        </p>
        {SUBJECT_FIELDS.map((f) => (
          <Field key={f.key} label={fieldLabel(f.label, f.key)} hint={`${f.hint} Default “${defaults?.[f.key] ?? ""}”.`}>
            <TextInput
              value={form?.[f.key] ?? ""}
              disabled={!form}
              maxLength={MAX_LINE}
              onChange={(e) => update({ [f.key]: e.target.value } as Partial<CopyForm>)}
            />
          </Field>
        ))}
      </Card>

      <Card title="Reminder & confirmation emails">
        <Field
          label={fieldLabel("App benefits", "benefits")}
          hint="One per line, up to 6 — the checklist of what guests get in the app."
        >
          <textarea
            value={form?.benefits ?? ""}
            disabled={!form}
            rows={4}
            onChange={(e) => update({ benefits: e.target.value })}
            className="w-full rounded-lg border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 placeholder:text-plum-700/35 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-plum-50 dark:placeholder:text-plum-200/25"
          />
        </Field>
        <Field
          label={fieldLabel("Button label", "ctaLabel")}
          hint="The main call-to-action in emails."
        >
          <TextInput
            value={form?.ctaLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ ctaLabel: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Team label", "teamLabel")}
          hint='The footer prefix on every guest email — "Event team: {host}".'
        >
          <TextInput
            value={form?.teamLabel ?? ""}
            disabled={!form}
            onChange={(e) => update({ teamLabel: e.target.value })}
          />
        </Field>
      </Card>

      <Card title="Invite landing page">
        <Field
          label={fieldLabel("Welcome line", "welcomeLine")}
          hint="The greeting on the invite landing page. {first name} fills in per guest and {event} with the event name; when a guest's name isn't known the greeting drops it (“Hi {first name} — you're invited” reads “You're invited”)."
        >
          <TextInput
            value={form?.welcomeLine ?? ""}
            disabled={!form}
            onChange={(e) => update({ welcomeLine: e.target.value })}
          />
        </Field>
        <Field
          label={fieldLabel("Step 3 label", "step3Label")}
          hint='The final step on the invite page, after "Get the Attend app".'
        >
          <TextInput
            value={form?.step3Label ?? ""}
            disabled={!form}
            onChange={(e) => update({ step3Label: e.target.value })}
          />
        </Field>
        <div className="pt-1">
          <GhostButton
            type="button"
            disabled={!form || !defaults || FIELDS.every((k) => !customized(k))}
            onClick={() => {
              if (defaults) {
                setDirty(true)
                setForm({ ...defaults })
              }
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset all to defaults
          </GhostButton>
        </div>
      </Card>

      <SectionPageFooter>
        <PrimaryButton onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save changes"}
        </PrimaryButton>
      </SectionPageFooter>
    </SectionPage>
  )
}
